import {
  Component,
  createSignal,
  createMemo,
  createEffect,
  untrack,
} from 'solid-js';
import { Layer } from '@trimble/web3d-providers';
import { ColorPickerPanel } from '@trimble/ui-components/solidjs';
import { RgbColor } from 'solid-color';
import { Portal } from 'solid-js/web';
import { t } from '@trimble/i18n';
import { useLayerPopup } from '../utils/layer-popup-utils';
import tinycolor from 'tinycolor2';
import { LineStyleSelector } from '../line-style-selector/LineStyleSelector';
import { LineWeightSelector } from '../line-weight-selector/LineWeightSelector';

export interface LayerEditPopupProps {
  layer: Layer;
  anchorElement: HTMLElement;
  onClose(): void;
  onNameChange(layerIds: number[], newName: string): void;
  onColorChange(layerIds: number[], color: string): void;
  onLineStyleChange(layerIds: number[], lineStyle: string): void;
  onLineThicknessChange(layerIds: number[], lineThickness: number): void;
}

type TextField = HTMLInputElement | HTMLTextAreaElement;

/**
 * Read live text from the layer name control (host + shadow) so we do not rely on
 * a Solid signal that can lag `inputChange` when the dialog closes on outside mousedown.
 */
function readLayerNameFromControl(root: ParentNode | undefined): string | null {
  if (!root) {
    return null;
  }
  const modus = root.querySelector('modus-wc-text-input, modus-text-input') as
    | (HTMLElement & { value?: string })
    | null;
  if (!modus) {
    return null;
  }
  const field =
    (modus.shadowRoot?.querySelector('input, textarea') as TextField | null) ??
    (modus.querySelector('input, textarea') as TextField | null);
  if (field) {
    return field.value;
  }
  if (typeof modus.value === 'string') {
    return modus.value;
  }
  const attrValue = modus.getAttribute('value');
  if (attrValue != null) {
    return attrValue;
  }
  return null;
}

/** `modus-wc-text-input` `inputChange` — value is `event.detail.target.value`. */
function readModusWcTextInputValue(event: Event): string {
  const detail = (event as CustomEvent<unknown>).detail;
  if (detail && typeof detail === 'object' && 'target' in detail) {
    const target = (detail as { target?: HTMLInputElement | null }).target;
    return target?.value ?? '';
  }
  return '';
}

export const LayerEditPopup: Component<LayerEditPopupProps> = (props) => {
  let popupRef: HTMLDivElement | undefined;
  let nameInputWrapper: HTMLDivElement | undefined;

  const [layerName, setLayerName] = createSignal<string>(
    untrack(() => props.layer.name)
  );
  const [currentSavedName, setCurrentSavedName] = createSignal<string>(
    untrack(() => props.layer.name)
  );

  const [isNameBlurHandling, setIsNameBlurHandling] = createSignal(false);
  const [isEnterNameCommitInProgress, setIsEnterNameCommitInProgress] =
    createSignal(false);

  /** Last known id while the popup is open — survives blur after parent clears `layer` during close. */
  const [stableLayerId, setStableLayerId] = createSignal(
    untrack(() => {
      const id = props.layer.id;
      return typeof id === 'number' ? id : Number.parseInt(String(id), 10) || 0;
    })
  );

  const layerId = createMemo(() => {
    const layer = props.layer;
    if (layer == null) {
      return stableLayerId();
    }
    const id = layer.id;
    return typeof id === 'number' ? id : Number.parseInt(String(id), 10) || 0;
  });

  let lastBoundLayerId: number | undefined;
  createEffect(() => {
    const layer = props.layer;
    if (layer == null) {
      return;
    }
    const id =
      typeof layer.id === 'number'
        ? layer.id
        : Number.parseInt(String(layer.id), 10) || 0;
    setStableLayerId(id);
    if (lastBoundLayerId === id) {
      return;
    }
    lastBoundLayerId = id;
    const name = layer.name;
    setLayerName(name);
    setCurrentSavedName(name);
  });

  const syncLayerNameFromTextInput = (): void => {
    const read = readLayerNameFromControl(nameInputWrapper ?? popupRef);
    if (read != null) {
      setLayerName(read);
    }
  };

  const handleClose = (): void => {
    // Outside-click commits the name in `onBeforeOutsideClose` (or via Modus `inputBlur` for other blur paths);
    // do not call `handleNameBlur` here: a final read on close can be stale w.r.t. Modus/read-only.
    setIsNameBlurHandling(false);
    props.onClose();
  };

  useLayerPopup({
    popupRef: () => popupRef,
    anchorElement: () => props.anchorElement,
    onClose: () => handleClose,
    onBeforeOutsideClose: () => () => {
      syncLayerNameFromTextInput();
      handleNameBlur();
    },
  });

  const handleNameBlur = (): 'committed' | 'reset' | 'noop' => {
    // Prevent duplicate synchronous calls
    if (isNameBlurHandling()) {
      return 'noop';
    }
    setIsNameBlurHandling(true);

    try {
      const liveName = readLayerNameFromControl(nameInputWrapper ?? popupRef);
      if (liveName != null) {
        setLayerName(liveName);
      }
      const newName = (liveName ?? layerName()).trim();
      // Compare to last committed name, not `props.layer.name` — the prop can lag the API
      // while `currentSavedName` is updated on every successful commit.
      const savedName = untrack(() => currentSavedName());
      if (newName !== savedName && newName.length > 0) {
        props.onNameChange([layerId()], newName);
        setCurrentSavedName(newName);
        return 'committed';
      } else if (newName.length === 0) {
        // Reset to last committed / bound name (`layer` may already be cleared during close)
        const currentName = untrack(() => currentSavedName());
        setLayerName(currentName);
        return 'reset';
      }
      return 'noop';
    } finally {
      // Reset flag after a microtask to allow async operations to complete
      queueMicrotask(() => {
        setIsNameBlurHandling(false);
      });
    }
  };

  const handleColorSave = (rgb: RgbColor): void => {
    const hexColor = tinycolor(rgb).toHexString();
    props.onColorChange([layerId()], hexColor);
  };

  return (
    <Portal>
      <div
        ref={(el) => {
          popupRef = el;
        }}
        data-testid="layer-edit-popup"
        class="fixed z-dropdown-elevated bg-white shadow-lg rounded-md border border-gray-2 p-3 w-[285px]"
      >
        <div class="flex flex-col gap-3">
          {/* Layer Name */}
          <div class="flex flex-col gap-1">
            <label class="text-xs text-gray-6">{t('general.name')}</label>
            <div
              ref={(el) => {
                nameInputWrapper = el;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const modusInput = nameInputWrapper?.querySelector(
                    'modus-wc-text-input, modus-text-input'
                  ) as HTMLElement | null;
                  const actualInput =
                    modusInput?.shadowRoot?.querySelector('input') ||
                    modusInput?.querySelector('input');
                  const directValue =
                    (
                      actualInput as
                        | HTMLInputElement
                        | HTMLTextAreaElement
                        | null
                    )?.value ??
                    (modusInput as (HTMLElement & { value?: string }) | null)
                      ?.value ??
                    modusInput?.getAttribute('value');
                  if (typeof directValue === 'string') {
                    setLayerName(directValue);
                  } else {
                    syncLayerNameFromTextInput();
                  }
                  const enterCommitResult = handleNameBlur();
                  const shouldSkipNextBlur = enterCommitResult !== 'noop';
                  setIsEnterNameCommitInProgress(shouldSkipNextBlur);
                  if (actualInput) {
                    actualInput.blur();
                  } else {
                    // No blur event will fire, so clear the Enter guard immediately.
                    setIsEnterNameCommitInProgress(false);
                  }
                }
              }}
            >
              {/* v2: inputChange (detail.target.value) + inputBlur. Legacy: valueChange + string detail. */}
              <modus-wc-text-input
                class="w-full"
                size="md"
                label=""
                bordered={true}
                value={layerName()}
                disabled={props.layer.isDefault}
                read-only={false}
                placeholder={t('shared.data-prep.enter-layer-name')}
                on:inputChange={(e) => {
                  setLayerName(readModusWcTextInputValue(e));
                }}
                on:inputBlur={() => {
                  // Enter commits explicitly on keydown and then triggers blur; skip this follow-up blur
                  // once so the same value is not committed twice.
                  if (isEnterNameCommitInProgress()) {
                    setIsEnterNameCommitInProgress(false);
                    return;
                  }
                  syncLayerNameFromTextInput();
                  handleNameBlur();
                }}
              />
            </div>
          </div>

          {/* Color Picker */}
          <div class="flex flex-col gap-1">
            <ColorPickerPanel
              initialColor={props.layer.color}
              objectColor={props.layer.color}
              disabled={false}
              noContainer={true}
              showResetButton={false}
              onSave={handleColorSave}
            />
          </div>

          {/* Line Weight and Line Style */}
          <div class="flex flex-row gap-2 mt-1">
            <LineWeightSelector
              initialLineThickness={props.layer.lineThickness ?? 1}
              onLineThicknessChange={(lt) =>
                props.onLineThicknessChange([layerId()], lt)
              }
            />
            <LineStyleSelector
              initialLineStyle={props.layer.lineStyle}
              onLineStyleChange={(ls) =>
                props.onLineStyleChange([layerId()], ls)
              }
            />
          </div>
        </div>
      </div>
    </Portal>
  );
};
